/**
 * Asset Service for WYSIWYG Editor
 * 
 * Handles uploading, fetching, resolving, and filtering static assets
 * (fonts and images) for the label editor.
 */

import type { AssetReference, StaticFile, AssetCategory } from './types';

// FileType enum matching backend
export const FileType = {
  Static: 0,
  ProductImage: 1,
  BrandLogo: 2,
  RegulatoryCardLogo: 3,
  LabTesting: 4,
} as const;

// MIME types for fonts
export const FONT_MIME_TYPES = [
  'font/ttf',
  'font/otf',
  'font/woff',
  'font/woff2',
  'application/x-font-ttf',
  'application/x-font-otf',
  'application/font-woff',
  'application/font-woff2',
  'application/vnd.ms-opentype',
  'application/x-font-truetype',
];

const IMAGE_FORMATS = [
  { extensions: ['.jpg', '.jpeg'], mimeTypes: ['image/jpeg', 'image/jpg'], uploadMimeType: 'image/jpeg' },
  { extensions: ['.png'], mimeTypes: ['image/png'], uploadMimeType: 'image/png' },
  { extensions: ['.svg'], mimeTypes: ['image/svg+xml'], uploadMimeType: 'image/svg+xml' },
] as const;

export const IMAGE_MIME_TYPES = IMAGE_FORMATS.flatMap(format => [...format.mimeTypes]);

// File extensions for upload input accept attribute
export const FONT_EXTENSIONS = '.ttf,.otf,.woff,.woff2';
export const IMAGE_EXTENSIONS = IMAGE_FORMATS.flatMap(format => [...format.extensions]).join(',');

/**
 * Get the file extension from a filename (lowercase, includes the dot)
 */
function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filename.slice(lastDot).toLowerCase();
}

/**
 * Check if a filename has a font extension
 */
function isFontExtension(filename: string): boolean {
  const ext = getFileExtension(filename);
  return ['.ttf', '.otf', '.woff', '.woff2'].includes(ext);
}

function getImageFormat(filename: string) {
  const extension = getFileExtension(filename);
  return IMAGE_FORMATS.find(format => format.extensions.some(candidate => candidate === extension));
}

/**
 * Get MIME type from file extension (fallback when browser doesn't provide one)
 */
function getMimeTypeFromExtension(filename: string): string {
  const ext = getFileExtension(filename);
  const mimeMap: Record<string, string> = {
    // Fonts
    '.ttf': 'font/ttf',
    '.otf': 'font/otf',
    '.woff': 'font/woff',
    '.woff2': 'font/woff2',
  };
  const imageFormat = getImageFormat(filename);
  return imageFormat?.uploadMimeType || mimeMap[ext] || 'application/octet-stream';
}

/**
 * Get the effective MIME type for a file
 * Uses browser-provided type if available, otherwise falls back to extension
 */
function getEffectiveMimeType(file: File): string {
  const imageFormat = getImageFormat(file.name);
  const mimeType = normalizeMimeType(file.type);
  if (
    imageFormat &&
    (!mimeType ||
      mimeType === 'application/octet-stream' ||
      imageFormat.mimeTypes.some(allowed => allowed === mimeType))
  ) {
    return imageFormat.uploadMimeType;
  }
  if (mimeType && mimeType !== 'application/octet-stream') {
    return mimeType;
  }
  return getMimeTypeFromExtension(file.name);
}

/**
 * Normalize a MIME type by decoding URL-encoded characters and removing parameters
 */
function normalizeMimeType(mimeType: string): string {
  let decoded: string;
  try {
    decoded = decodeURIComponent(mimeType);
  } catch {
    decoded = mimeType;
  }
  return decoded.split(';', 1)[0].trim().toLowerCase();
}

/**
 * Determine if a MIME type is a font
 */
export function isFontMimeType(mimeType: string): boolean {
  const normalized = normalizeMimeType(mimeType);
  return FONT_MIME_TYPES.some(mt => normalized.includes(mt) || normalized.includes('font'));
}

/**
 * Determine if a MIME type is an image
 */
export function isImageMimeType(mimeType: string): boolean {
  const normalized = normalizeMimeType(mimeType);
  return IMAGE_MIME_TYPES.some(mt => normalized.includes(mt));
}

/**
 * Get the category of an asset based on its MIME type
 */
export function getAssetCategory(mimeType: string): AssetCategory | null {
  if (isFontMimeType(mimeType)) return 'font';
  if (isImageMimeType(mimeType)) return 'image';
  return null;
}

/**
 * Filter assets by category
 */
export function filterByCategory(assets: StaticFile[], category: AssetCategory): StaticFile[] {
  return assets.filter(asset => {
    const assetCategory = getAssetCategory(asset.mimeType);
    return assetCategory === category;
  });
}

/**
 * Sort assets by MIME type (groups fonts and images together)
 */
export function sortByMimeType(assets: StaticFile[]): StaticFile[] {
  return [...assets].sort((a, b) => {
    const catA = getAssetCategory(a.mimeType);
    const catB = getAssetCategory(b.mimeType);
    if (catA === catB) {
      return a.displayName.localeCompare(b.displayName);
    }
    // Fonts first, then images, then others
    if (catA === 'font') return -1;
    if (catB === 'font') return 1;
    if (catA === 'image') return -1;
    if (catB === 'image') return 1;
    return 0;
  });
}

// Type for the API object (subset of what we need)
export type AssetApi = {
  getFileId: (params: { organizationId: number; fileType: number; mimeType: string; file: File }) => Promise<string>;
  getStaticFiles: (params: { organizationId: number; page?: number; pageSize?: number }) => Promise<StaticFile[]>;
  getFileLink: (params: { organizationId: number; fileType: number; name: string }) => Promise<string>;
  getFileContentUrl: (params: { organizationId: number; fileType: number; blobName: string }) => string;
};

// Cache for resolved asset URLs (SAS URLs have limited lifetime)
const urlCache = new Map<string, { url: string; expiresAt: number }>();
const URL_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes (SAS URLs last 24 hours, but we refresh early)

/**
 * Upload an asset file and return an AssetReference
 */
export async function uploadAsset(
  api: AssetApi,
  organizationId: number,
  file: File
): Promise<AssetReference> {
  // Get effective MIME type (fallback to extension-based detection for fonts)
  const mimeType = getEffectiveMimeType(file);
  
  const fileId = await api.getFileId({
    organizationId,
    fileType: FileType.Static,
    mimeType,
    file,
  });

  // The API returns the file ID as a string, parse it
  const parsedFileId = parseInt(fileId, 10);
  
  // For the blob name, we use the file ID (backend generates UUID v7)
  // We'll need to fetch the actual blob name from the static files list
  // For now, use the file ID as a placeholder - it will be resolved when fetching
  return {
    fileId: parsedFileId,
    blobName: fileId, // Will be the actual blob name from backend
    displayName: file.name,
    mimeType, // Use the effective MIME type, not file.type
  };
}

/**
 * Fetch all static files for an organization
 */
export async function fetchAssets(
  api: AssetApi,
  organizationId: number,
  page = 0,
  pageSize = 100
): Promise<StaticFile[]> {
  if (!api?.getStaticFiles) {
    throw new Error('Asset listing is unavailable');
  }

  // Let the picker distinguish a failed request from a successful empty list.
  const files = await api.getStaticFiles({ organizationId, page, pageSize });
  return files || [];
}

/**
 * Resolve an asset blob name to a SAS URL
 */
export async function resolveAssetUrl(
  api: AssetApi,
  organizationId: number,
  blobName: string
): Promise<string> {
  const cacheKey = `${organizationId}:${blobName}`;
  const cached = urlCache.get(cacheKey);
  
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url;
  }

  try {
    const url = await api.getFileLink({
      organizationId,
      fileType: FileType.Static,
      name: blobName,
    });

    urlCache.set(cacheKey, {
      url,
      expiresAt: Date.now() + URL_CACHE_TTL_MS,
    });

    return url;
  } catch (error) {
    console.error('[assetService] Failed to resolve asset URL:', error);
    throw error;
  }
}

/**
 * Clear the URL cache (useful when switching organizations)
 */
export function clearAssetCache(): void {
  urlCache.clear();
  contentCache.clear();
  contentFailureCache.clear();
  contentInFlight.clear();
}

// Session-lifetime caches for fetchAssetContent. Successes are cached as base64
// data URLs; failures are cached as a set of "orgId|blobName" keys so we don't
// re-hit the backend for known-missing assets (which previously caused 404
// console floods when parent components re-rendered).
const contentCache = new Map<string, string>();
const contentFailureCache = new Set<string>();
const contentInFlight = new Map<string, Promise<string | null>>();

function contentCacheKey(organizationId: number, blobName: string): string {
  return `${organizationId}|${blobName}`;
}

/**
 * Fetch file content via backend proxy and return as base64 data URL
 * This avoids CORS issues with Azure Blob Storage.
 *
 * Includes in-flight request deduplication and session-lifetime caching of
 * both successes and failures to prevent repeated network requests for the
 * same blob across component re-renders.
 */
export async function fetchAssetContent(
  api: AssetApi,
  organizationId: number,
  blobName: string,
  getToken: () => string | null
): Promise<string | null> {
  const key = contentCacheKey(organizationId, blobName);

  // Known-success: return cached base64.
  const cached = contentCache.get(key);
  if (cached) return cached;

  // Known-failure: return null silently (no network, no log).
  if (contentFailureCache.has(key)) return null;

  // In-flight: share the existing promise.
  const inFlight = contentInFlight.get(key);
  if (inFlight) return inFlight;

  const token = getToken();
  if (!token) {
    console.warn('[assetService] No auth token available');
    return null;
  }

  const url = api.getFileContentUrl({
    organizationId,
    fileType: FileType.Static,
    blobName,
  });

  const promise = (async (): Promise<string | null> => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.warn(`[assetService] Failed to fetch content for ${blobName}: ${response.status}`);
        // Only cache permanent "asset doesn't exist" statuses. Transient
        // errors (401/403/5xx) should be retried on subsequent template loads.
        if (response.status === 404 || response.status === 410) {
          contentFailureCache.add(key);
        }
        return null;
      }

      const blob = await response.blob();
      const base64 = await blobToBase64(blob);
      contentCache.set(key, base64);
      return base64;
    } catch (error) {
      // Don't cache thrown errors: network blips, aborts, etc. are transient
      // and should be retried.
      console.error(`[assetService] Error fetching content for ${blobName}:`, error);
      return null;
    } finally {
      contentInFlight.delete(key);
    }
  })();

  contentInFlight.set(key, promise);
  return promise;
}

/**
 * Convert a Blob to a base64 data URL
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert blob to base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert a StaticFile to an AssetReference
 */
export function staticFileToAssetReference(file: StaticFile): AssetReference {
  return {
    fileId: file.id,
    blobName: file.blobName,
    displayName: file.displayName,
    mimeType: file.mimeType,
  };
}

/**
 * Get the accept attribute value for file input based on category
 */
export function getAcceptAttribute(category: AssetCategory): string {
  if (category === 'font') {
    return `${FONT_EXTENSIONS},${FONT_MIME_TYPES.join(',')}`;
  }
  return `${IMAGE_EXTENSIONS},${IMAGE_MIME_TYPES.join(',')}`;
}

/**
 * Validate that a file matches the expected category
 * Uses MIME type first, then falls back to file extension
 * (browsers often misreport font MIME types as empty or application/octet-stream)
 */
export function validateFileCategory(file: File, expectedCategory: AssetCategory): boolean {
  if (expectedCategory === 'image') {
    const imageFormat = getImageFormat(file.name);
    if (!imageFormat) return false;

    const mimeType = normalizeMimeType(file.type);
    return (
      !mimeType ||
      mimeType === 'application/octet-stream' ||
      imageFormat.mimeTypes.some(allowed => allowed === mimeType)
    );
  }

  // First try MIME type detection
  const actualCategory = getAssetCategory(file.type);
  if (actualCategory === expectedCategory) return true;
  
  // Fall back to file extension check (especially needed for fonts)
  if (expectedCategory === 'font') {
    return isFontExtension(file.name);
  }
  return false;
}

export function getInvalidFileMessage(category: AssetCategory): string {
  return category === 'image'
    ? 'Please select a JPG/JPEG, PNG, or SVG image file.'
    : `Please select a valid ${getCategoryLabel(category).toLowerCase()} file.`;
}

/**
 * Get a human-readable label for an asset category
 */
export function getCategoryLabel(category: AssetCategory): string {
  return category === 'font' ? 'Font' : 'Image';
}

/**
 * Get the file icon based on category
 */
export function getCategoryIcon(category: AssetCategory): string {
  return category === 'font' ? 'Aa' : '';
}
